import type { SearchMode } from 'lib/semantic-search/constants';
import pushToDataLayer from './pushToDataLayer';

type SearchContext = 'Collection Objects' | 'Site Search';
type SearchOrigin =
	| 'Global Nav'
	| 'Collection Landing'
	| 'Collection Search'
	| 'Site Search Results';
type SearchAction = 'clear_all' | 'filter' | 'paginate';

interface SearchMetricBase {
	searchContext: SearchContext;
	searchMode?: SearchMode;
	searchOrigin: SearchOrigin;
	searchQuerystring: string;
}

interface SearchActionMetric extends SearchMetricBase {
	filterName?: string;
	searchAction: SearchAction;
}

interface SearchResultsMetric extends SearchMetricBase {
	queryTookMs?: number;
	resultsCount: number;
}

interface SearchResultClickMetric extends SearchMetricBase {
	objectId?: string | number;
	objectTitle?: string;
	resultRank: number;
	resultTitle?: string;
	resultType?: string;
	resultUrl?: string;
}

// Strip params that aren't part of the search state:
// '_' for Next.js internals (_rsc, __next*) and tracking linkers (_gl),
// 'gtm_' and 'utm_' cover GTM debug + campaign attribution.
// Note: Better to move to an include/santized list rather than exclude list.
const EXCLUDED_PARAM_PREFIXES = ['_', 'gtm_', 'utm_'];

export const stripNonSearchParams = (querystring: string) => {
	const params = new URLSearchParams(querystring);

	for (const key of [...params.keys()]) {
		if (EXCLUDED_PARAM_PREFIXES.some((prefix) => key.startsWith(prefix))) {
			params.delete(key);
		}
	}

	return params.toString();
};

const getSearchTermFromQuerystring = (searchQuerystring: string) =>
	new URLSearchParams(searchQuerystring).get('q') || '';

const getSearchQuerystringFromTerm = ({
	searchTerm,
}: {
	searchTerm?: string | null;
}) => {
	const trimmedSearchTerm = searchTerm?.trim();

	if (!trimmedSearchTerm) {
		return undefined;
	}

	return new URLSearchParams({ q: trimmedSearchTerm }).toString();
};

const hasMeaningfulSearchState = (searchQuerystring: string) => {
	// Ignore empty state and the default first-page offset.
	// Tracking params are already stripped.
	for (const [key, value] of new URLSearchParams(searchQuerystring)) {
		if (value && (key !== 'offset' || value !== '0')) {
			return true;
		}
	}

	return false;
};

const normalizeCollectionFilterName = (filterName: string) => {
	// 'q' submits are tracked elsewhere, 'explain' is dev-only
	if (filterName === 'q' || filterName === 'explain') {
		return null;
	}

	// normalize year-range filters to single analytics name
	if (filterName === 'yearFrom' || filterName === 'yearTo') {
		return 'yearRange';
	}

	return filterName;
};

const emitSearchActionMetric = ({
	filterName,
	searchAction,
	searchContext,
	searchMode,
	searchOrigin,
	searchQuerystring,
}: SearchActionMetric) => {
	const cleanedQuerystring = stripNonSearchParams(searchQuerystring);
	const rawSearchTerm = getSearchTermFromQuerystring(cleanedQuerystring);

	pushToDataLayer({
		event: 'search',
		Search_Context: searchContext,
		Search_Origin: searchOrigin,
		Search_Action: searchAction,
		Search_Querystring: cleanedQuerystring,
		...(searchMode ? { Search_Mode: searchMode } : {}),
		...(rawSearchTerm ? { Search_Term: rawSearchTerm } : {}),
		...(searchAction === 'filter' && filterName
			? { Filter_Name: filterName }
			: {}),
	});
};

const emitSearchSubmittedMetric = ({
	searchContext,
	searchMode,
	searchOrigin,
	searchTerm,
}: {
	searchContext: SearchContext;
	searchMode?: SearchMode;
	searchOrigin: SearchOrigin;
	searchTerm?: string | null;
}) => {
	const searchQuerystring = getSearchQuerystringFromTerm({ searchTerm });
	const trimmedSearchTerm = searchTerm?.trim();

	if (searchQuerystring === undefined) {
		return;
	}

	pushToDataLayer({
		event: 'search',
		Search_Context: searchContext,
		Search_Origin: searchOrigin,
		Search_Action: 'query',
		Search_Querystring: searchQuerystring,
		Search_Term: trimmedSearchTerm,
		...(searchMode ? { Search_Mode: searchMode } : {}),
	});
};

const emitSearchResultsMetric = ({
	queryTookMs,
	resultsCount,
	searchContext,
	searchMode,
	searchOrigin,
	searchQuerystring,
}: SearchResultsMetric) => {
	const cleanedQuerystring = stripNonSearchParams(searchQuerystring);
	const rawSearchTerm = getSearchTermFromQuerystring(cleanedQuerystring);

	if (!hasMeaningfulSearchState(cleanedQuerystring)) {
		return;
	}

	pushToDataLayer({
		event: 'search_results',
		Search_Context: searchContext,
		Search_Origin: searchOrigin,
		Search_Querystring: cleanedQuerystring,
		Results_Count: resultsCount,
		...(searchMode ? { Search_Mode: searchMode } : {}),
		...(typeof queryTookMs === 'number' ? { Query_Took_MS: queryTookMs } : {}),
		...(rawSearchTerm ? { Search_Term: rawSearchTerm } : {}),
	});
};

const emitSearchResultClickMetric = ({
	objectId,
	objectTitle,
	resultRank,
	resultTitle,
	resultType,
	resultUrl,
	searchContext,
	searchMode,
	searchOrigin,
	searchQuerystring,
}: SearchResultClickMetric) => {
	const cleanedQuerystring = stripNonSearchParams(searchQuerystring);
	const rawSearchTerm = getSearchTermFromQuerystring(cleanedQuerystring);

	pushToDataLayer({
		event: 'search_result_click',
		Search_Context: searchContext,
		Search_Origin: searchOrigin,
		Search_Querystring: cleanedQuerystring,
		Result_Rank: resultRank,
		...(searchMode ? { Search_Mode: searchMode } : {}),
		...(rawSearchTerm ? { Search_Term: rawSearchTerm } : {}),
		...(objectId !== undefined ? { Object_ID: String(objectId) } : {}),
		...(objectTitle ? { Object_Title: objectTitle } : {}),
		...(resultTitle ? { Result_Title: resultTitle } : {}),
		...(resultType ? { Result_Type: resultType } : {}),
		...(resultUrl ? { Result_URL: resultUrl } : {}),
	});
};

export const trackCollectionSearchSubmitted = ({
	searchMode,
	searchOrigin,
	searchTerm,
}: {
	searchMode?: SearchMode;
	searchOrigin: SearchOrigin;
	searchTerm?: string | null;
}) => {
	emitSearchSubmittedMetric({
		searchContext: 'Collection Objects',
		searchMode,
		searchOrigin,
		searchTerm,
	});
};

export const trackCollectionSearchResultsLoaded = ({
	queryTookMs,
	resultsCount,
	searchMode,
	searchQuerystring,
}: {
	queryTookMs?: number;
	resultsCount: number;
	searchMode?: SearchMode;
	searchQuerystring: string;
}) => {
	emitSearchResultsMetric({
		queryTookMs,
		resultsCount,
		searchContext: 'Collection Objects',
		searchMode,
		searchOrigin: 'Collection Search',
		searchQuerystring,
	});
};

export const trackCollectionSearchClearedAll = ({
	searchMode,
	searchQuerystring,
}: {
	searchMode?: SearchMode;
	searchQuerystring: string;
}) => {
	emitSearchActionMetric({
		searchAction: 'clear_all',
		searchContext: 'Collection Objects',
		searchMode,
		searchOrigin: 'Collection Search',
		searchQuerystring,
	});
};

export const trackCollectionSearchFilterChanged = ({
	filterName,
	searchMode,
	searchQuerystring,
}: {
	filterName: string;
	searchMode?: SearchMode;
	searchQuerystring: string;
}) => {
	const normalizedFilterName = normalizeCollectionFilterName(filterName);

	if (!normalizedFilterName) {
		return;
	}

	emitSearchActionMetric({
		filterName: normalizedFilterName,
		searchAction: 'filter',
		searchContext: 'Collection Objects',
		searchMode,
		searchOrigin: 'Collection Search',
		searchQuerystring,
	});
};

export const trackCollectionSearchPaginated = ({
	searchMode,
	searchQuerystring,
}: {
	searchMode?: SearchMode;
	searchQuerystring: string;
}) => {
	emitSearchActionMetric({
		searchAction: 'paginate',
		searchContext: 'Collection Objects',
		searchMode,
		searchOrigin: 'Collection Search',
		searchQuerystring,
	});
};

export const trackCollectionSearchResultClicked = ({
	objectId,
	objectTitle,
	resultRank,
	searchMode,
	searchQuerystring,
}: {
	objectId?: string | number;
	objectTitle?: string;
	resultRank: number;
	searchMode?: SearchMode;
	searchQuerystring: string;
}) => {
	emitSearchResultClickMetric({
		objectId,
		objectTitle,
		resultRank,
		resultType: 'object',
		searchContext: 'Collection Objects',
		searchMode,
		searchOrigin: 'Collection Search',
		searchQuerystring,
	});
};

export const trackCollectionSearchTipsOpened = ({
	searchQuerystring,
}: {
	searchQuerystring: string;
}) => {
	const cleanedQuerystring = stripNonSearchParams(searchQuerystring);
	const rawSearchTerm = getSearchTermFromQuerystring(cleanedQuerystring);

	pushToDataLayer({
		event: 'search_tips_open',
		Search_Context: 'Collection Objects',
		Search_Origin: 'Collection Search',
		Search_Querystring: cleanedQuerystring,
		...(rawSearchTerm ? { Search_Term: rawSearchTerm } : {}),
	});
};

export const trackSiteSearchFacetChanged = ({
	searchQuerystring,
}: {
	searchQuerystring: string;
}) => {
	emitSearchActionMetric({
		filterName: 'searchFacet',
		searchAction: 'filter',
		searchContext: 'Site Search',
		searchOrigin: 'Site Search Results',
		searchQuerystring,
	});
};

export const trackSiteSearchSubmitted = ({
	searchOrigin,
	searchTerm,
}: {
	searchOrigin: SearchOrigin;
	searchTerm?: string | null;
}) => {
	emitSearchSubmittedMetric({
		searchContext: 'Site Search',
		searchOrigin,
		searchTerm,
	});
};

export const trackSiteSearchPaginated = ({
	searchQuerystring,
}: {
	searchQuerystring: string;
}) => {
	emitSearchActionMetric({
		searchAction: 'paginate',
		searchContext: 'Site Search',
		searchOrigin: 'Site Search Results',
		searchQuerystring,
	});
};

export const trackSiteSearchResultsLoaded = ({
	resultsCount,
	searchQuerystring,
}: {
	resultsCount: number;
	searchQuerystring: string;
}) => {
	emitSearchResultsMetric({
		resultsCount,
		searchContext: 'Site Search',
		searchOrigin: 'Site Search Results',
		searchQuerystring,
	});
};

export const trackSiteSearchResultClicked = ({
	objectId,
	objectTitle,
	resultRank,
	resultTitle,
	resultType,
	resultUrl,
	searchQuerystring,
}: {
	objectId?: string | number;
	objectTitle?: string;
	resultRank: number;
	resultTitle?: string;
	resultType?: string;
	resultUrl?: string;
	searchQuerystring: string;
}) => {
	emitSearchResultClickMetric({
		objectId,
		objectTitle,
		resultRank,
		resultTitle,
		resultType,
		resultUrl,
		searchContext: 'Site Search',
		searchOrigin: 'Site Search Results',
		searchQuerystring,
	});
};
